import { useState, useEffect, useMemo } from 'react';
import {
  TrendingUp, Sparkles, AlertTriangle, Loader2, Target, Zap, Info,
  BarChart3, Search, BookOpen, ChevronDown, ChevronUp, CheckCircle2,
  Rocket, Coins, Gem, Building2,
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getLaunchForecastOptions, runLaunchVolumeForecast } from '../services/api';
import { InfoTip } from '../components/ui';

const MC_PRESETS = [
  { label: '$1M',   value: 1_000_000 },
  { label: '$5M',   value: 5_000_000 },
  { label: '$10M',  value: 10_000_000 },
  { label: '$25M',  value: 25_000_000 },
  { label: '$50M',  value: 50_000_000 },
  { label: '$100M', value: 100_000_000 },
];

// Launch scenarios — pick a starting pattern that matches your project.
// Each preset fills sector + market cap + listing tier together so users
// don't have to guess three independent inputs.
const SCENARIO_PRESETS = [
  {
    id: 'utility',
    label: 'Utility / platform token',
    icon: Zap,
    accent: 'cyan',
    blurb: 'Powers a product or platform. Often launches DEX-first then adds CEXes.',
    inputs: { categorySlug: 'infrastructure', targetMarketCapUsd: 5_000_000, listingTier: 'one_cex' },
  },
  {
    id: 'defi',
    label: 'DeFi protocol',
    icon: Coins,
    accent: 'emerald',
    blurb: 'Lending, DEX, derivatives. Usually higher MC and more CEX listings.',
    inputs: { categorySlug: 'decentralized-finance-defi', targetMarketCapUsd: 25_000_000, listingTier: 'few_cex' },
  },
  {
    id: 'memecoin',
    label: 'Memecoin / community',
    icon: Rocket,
    accent: 'amber',
    blurb: 'Community-driven launch, typically DEX-only initially.',
    inputs: { categorySlug: 'meme-token', targetMarketCapUsd: 1_000_000, listingTier: 'dex_only' },
  },
  {
    id: 'gamefi',
    label: 'GameFi / NFT project',
    icon: Gem,
    accent: 'purple',
    blurb: 'In-game economy, NFTs, virtual assets. Mid-range MC.',
    inputs: { categorySlug: 'gaming', targetMarketCapUsd: 10_000_000, listingTier: 'few_cex' },
  },
  {
    id: 'tier1',
    label: 'Major launch — tier-1 CEX',
    icon: Building2,
    accent: 'emerald',
    blurb: 'Established team, audited, regulated. Targeting Binance/Coinbase from day one.',
    inputs: { categorySlug: 'infrastructure', targetMarketCapUsd: 50_000_000, listingTier: 'tier1' },
  },
];

function fmtUsd(v, opts = {}) {
  if (v == null || !Number.isFinite(v)) return '—';
  const n = Number(v);
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(opts.k || 1)}k`;
  return `$${n.toFixed(opts.dp || 0)}`;
}

export default function LaunchVolumeForecasterPage() {
  const [options, setOptions] = useState(null);
  const [categorySlug, setCategorySlug] = useState('infrastructure');
  const [targetMarketCapUsd, setTargetMarketCapUsd] = useState(10_000_000);
  const [listingTier, setListingTier] = useState('dex_only');
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(true);
  const [appliedPreset, setAppliedPreset] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await getLaunchForecastOptions();
        setOptions(res?.data || res);
      } catch (e) {
        setError(e?.message || 'Failed to load options');
      }
    })();
  }, []);

  function applyPreset(preset) {
    setAppliedPreset(preset.id);
    setCategorySlug(preset.inputs.categorySlug);
    setTargetMarketCapUsd(preset.inputs.targetMarketCapUsd);
    setListingTier(preset.inputs.listingTier);
  }

  async function run() {
    setError(null);
    setLoading(true);
    try {
      const res = await runLaunchVolumeForecast({
        categorySlug, targetMarketCapUsd: Number(targetMarketCapUsd), listingTier,
      });
      setForecast(res?.data || res);
    } catch (e) {
      setError(e?.message || 'Forecast failed');
    } finally {
      setLoading(false);
    }
  }

  const chartData = useMemo(() => {
    if (!forecast?.forecast?.series) return [];
    // Reverse so day 0 (most recent) is on the right
    return forecast.forecast.series.map(s => ({
      ...s,
      label: `D-${s.daysAgo}`,
    })).reverse();
  }, [forecast]);

  // Strongest comparable for the plain-English callout
  const topComparable = useMemo(() => {
    if (!forecast?.comparablesUsed?.length) return null;
    return [...forecast.comparablesUsed].sort((a, b) => b.median30dVolumeUsd - a.median30dVolumeUsd)[0];
  }, [forecast]);

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-amber-500/10 via-dark-800/80 to-dark-900 rounded-2xl border border-amber-500/20 p-6">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-amber-400/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-amber-500/20 border border-amber-400/30 flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-white">Launch Volume Forecaster</h1>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-mono">Real comparables · No estimates</span>
            </div>
            <p className="text-xs text-amber-200/70 mt-1 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Elite · Issuer Tools · What organic daily volume can tokens like yours realistically produce?
              <InfoTip side="bottom">Real named comparables, not vibes. P10/P50/P90 bands across 30d real volume. Listing-tier multiplier swings 2x-20x. NOT a guarantee — real launch depends on execution + market conditions.</InfoTip>
            </p>
          </div>
        </div>
      </div>

      {/* What is this tool? */}
      <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 overflow-hidden">
        <button
          onClick={() => setAboutOpen(o => !o)}
          className="w-full px-5 py-4 flex items-center justify-between hover:bg-dark-700/30 transition"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-amber-400" />
            </div>
            <div className="text-left">
              <div className="text-sm font-semibold text-white">What is this tool — and how do I use it?</div>
              <div className="text-[11px] text-gray-500">Plain-English walkthrough · 30 seconds</div>
            </div>
          </div>
          {aboutOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>

        {aboutOpen && (
          <div className="px-5 pb-5 border-t border-dark-700/40 pt-4 space-y-4">
            <div>
              <div className="text-xs font-semibold text-amber-300 uppercase tracking-wider mb-1.5">The problem</div>
              <p className="text-sm text-gray-300 leading-relaxed">
                You're about to launch a token (or pitching one to investors / market makers) and need a defensible answer to: <span className="text-white font-semibold">"what daily volume will it actually trade?"</span> Pulling random numbers out of a deck is a credibility killer; you need a real basis.
              </p>
            </div>
            <div>
              <div className="text-xs font-semibold text-amber-300 uppercase tracking-wider mb-1.5">What this tool does</div>
              <p className="text-sm text-gray-300 leading-relaxed">
                Finds <span className="text-white">real comparable tokens</span> (same sector, similar market cap) and pulls the <span className="text-white">last 30 days</span> of their actual volume. Returns a P10–P50–P90 distribution so you see the full range, plus a multiplier for the CEX tier you're targeting. Every comparable is named so you can sanity-check the basis.
              </p>
            </div>
            <div>
              <div className="text-xs font-semibold text-amber-300 uppercase tracking-wider mb-1.5">How to use it in 3 steps</div>
              <ol className="text-sm text-gray-300 leading-relaxed space-y-1.5 list-decimal pl-5">
                <li>Pick a <span className="text-white">scenario preset</span> below that matches your launch — sets sector, market cap, and listing tier together.</li>
                <li>Adjust any of the three inputs if your launch deviates from the preset pattern.</li>
                <li>Click <span className="text-white">Forecast volume</span>. You'll get daily P50/P10/P90 bands, a 30-day chart, and the actual comparables that produced the numbers.</li>
              </ol>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2">
              <Info className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-200/90 leading-relaxed">
                <span className="font-semibold">How to read this:</span> This is what tokens LIKE YOURS currently produce — not a guarantee for your specific launch. Real launch volume depends on marketing, market conditions, listing relationships, and many factors no automated tool can predict. The listing-tier multiplier is empirical; actual CEX listings can produce 2×–20× volume swings.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Scenario presets */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-bold text-white">Pick a launch scenario</h2>
            <p className="text-[11px] text-gray-500 mt-0.5">Each preset sets sector + market cap + listing tier in one click. Tweak from there.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {SCENARIO_PRESETS.map(p => {
            const Icon = p.icon;
            const isActive = appliedPreset === p.id;
            const accents = {
              amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-300', icon: 'text-amber-400', activeBorder: 'border-amber-500/60' },
              cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', text: 'text-cyan-300', icon: 'text-cyan-400', activeBorder: 'border-cyan-500/60' },
              emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-300', icon: 'text-emerald-400', activeBorder: 'border-emerald-500/60' },
              purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-300', icon: 'text-purple-400', activeBorder: 'border-purple-500/60' },
            }[p.accent];
            return (
              <button
                key={p.id}
                onClick={() => applyPreset(p)}
                className={`text-left p-3 rounded-xl border bg-dark-800/60 hover:bg-dark-700/40 transition ${isActive ? accents.activeBorder : 'border-dark-700/50'}`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <div className={`w-6 h-6 rounded-md ${accents.bg} ${accents.border} border flex items-center justify-center`}>
                    <Icon className={`w-3 h-3 ${accents.icon}`} />
                  </div>
                  <div className={`text-[11px] font-semibold ${accents.text}`}>{p.label}</div>
                  {isActive && <CheckCircle2 className={`w-3 h-3 ${accents.icon} ml-auto`} />}
                </div>
                <p className="text-[10px] text-gray-500 leading-snug mb-1.5">{p.blurb}</p>
                <div className="text-[10px] text-gray-400 font-mono">${(p.inputs.targetMarketCapUsd / 1e6).toFixed(0)}M · {p.inputs.listingTier === 'dex_only' ? 'DEX' : p.inputs.listingTier === 'tier1' ? 'Tier-1' : 'CEX'}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Inputs */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5 space-y-4">
            <h2 className="text-sm font-bold text-white flex items-center gap-2">
              <Target className="w-4 h-4 text-amber-400" /> Your launch plan
            </h2>

            {/* Category */}
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Sector / category</label>
              <div className="grid grid-cols-3 gap-1.5">
                {(options?.categories || []).map(c => (
                  <button
                    key={c.slug}
                    onClick={() => setCategorySlug(c.slug)}
                    className={`px-2 py-1.5 rounded-md text-[11px] transition ${
                      categorySlug === c.slug
                        ? 'bg-amber-500/20 text-amber-300 border border-amber-400/40'
                        : 'bg-dark-700/60 text-gray-400 hover:text-white border border-dark-600'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-gray-500 mt-1.5">Pick the category closest to your token's primary purpose. Utility tokens that power a platform go in <span className="text-white">Infrastructure / Utility</span>.</p>
            </div>

            {/* Market cap target */}
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Target market cap</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {MC_PRESETS.map(p => (
                  <button
                    key={p.value}
                    onClick={() => setTargetMarketCapUsd(p.value)}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-mono transition ${
                      targetMarketCapUsd === p.value
                        ? 'bg-amber-500/20 text-amber-300 border border-amber-400/40'
                        : 'bg-dark-700/60 text-gray-400 hover:text-white border border-dark-600'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <input
                type="number"
                value={targetMarketCapUsd}
                onChange={e => setTargetMarketCapUsd(Number(e.target.value) || 0)}
                className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-amber-400/40 focus:outline-none"
              />
              <p className="text-[10px] text-gray-500 mt-1">We pull comparables in ±4× this range so you see a meaningful sample. Aim for what you realistically expect day-90, not the marketing peak.</p>
            </div>

            {/* Listing tier */}
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Planned exchange listings</label>
              <div className="space-y-1.5">
                {(options?.listingTiers || []).map(t => (
                  <button
                    key={t.id}
                    onClick={() => setListingTier(t.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-[11px] transition border ${
                      listingTier === t.id
                        ? 'bg-amber-500/20 text-amber-300 border-amber-400/40'
                        : 'bg-dark-700/40 text-gray-400 hover:text-white border-dark-600'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span>{t.label}</span>
                      <span className="text-[10px] font-mono">{t.multiplier}×</span>
                    </div>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-gray-500 mt-1">Multiplier applied to the comparable-token baseline. Empirical observation across hundreds of launches, not a guaranteed outcome.</p>
            </div>

            <button
              onClick={run}
              disabled={loading || !categorySlug || targetMarketCapUsd <= 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-amber-500 text-dark-900 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed font-bold text-sm transition"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {loading ? 'Forecasting…' : 'Forecast volume'}
            </button>

            {error && (
              <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-2.5 text-xs text-red-300">
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Output */}
        <div className="lg:col-span-2 space-y-4">
          {!forecast && !loading && (
            <div className="bg-dark-800/40 rounded-2xl border border-dark-700/40 border-dashed p-10 text-center">
              <Search className="w-10 h-10 text-amber-400/40 mx-auto mb-3" />
              <p className="text-sm text-gray-300 font-medium">Ready when you are.</p>
              <p className="text-[12px] text-gray-500 mt-2 max-w-md mx-auto leading-relaxed">
                Pick a scenario above or set your own sector + market cap + listing tier, then click <span className="text-white font-semibold">Forecast volume</span>. We'll find real comparable tokens and project realistic daily volume bands based on their actual recent trading activity.
              </p>
            </div>
          )}

          {loading && (
            <div className="bg-dark-800/40 rounded-2xl border border-dark-700/40 p-10 text-center">
              <Loader2 className="w-8 h-8 text-amber-400 mx-auto mb-3 animate-spin" />
              <p className="text-sm text-gray-400">Pulling 30 days of real volume from comparable tokens…</p>
              <p className="text-[11px] text-gray-500 mt-2">This takes 5–15 seconds — we fetch live market history per comparable, not pre-computed estimates.</p>
            </div>
          )}

          {forecast && !forecast.success && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-amber-300">Not enough comparables</p>
                  <p className="text-xs text-amber-200/80 mt-1">{forecast.message}</p>
                </div>
              </div>
            </div>
          )}

          {forecast?.success && (
            <>
              {/* Headline stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <StatCard
                  label="Median daily volume"
                  value={fmtUsd(forecast.forecast.dailyVolumeP50Usd)}
                  subtext={`based on ${forecast.dataSource.comparablesWithHistory} comparables`}
                  accent="amber"
                />
                <StatCard
                  label="Range (P10 – P90)"
                  value={`${fmtUsd(forecast.forecast.dailyVolumeP10Usd)} – ${fmtUsd(forecast.forecast.dailyVolumeP90Usd)}`}
                  subtext="80% of comparable tokens fall in this band"
                  accent="cyan"
                />
                <StatCard
                  label="Monthly projection"
                  value={fmtUsd(forecast.forecast.monthlyVolumeP50Usd)}
                  subtext={`${forecast.listingTier.multiplier}× ${forecast.listingTier.label} multiplier applied`}
                  accent="emerald"
                />
              </div>

              {/* Plain-English summary */}
              <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-500/30 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sparkles className="w-4 h-4 text-amber-300" />
                  </div>
                  <div className="space-y-2 flex-1">
                    <h3 className="text-sm font-bold text-white">What this means for your launch</h3>
                    <p className="text-xs text-gray-300 leading-relaxed">
                      Based on <span className="text-white font-semibold">{forecast.dataSource.comparablesWithHistory} real comparable tokens</span> in <span className="text-white font-semibold">{forecast.category.label}</span> near <span className="text-white font-mono">{fmtUsd(forecast.targetMarketCapUsd)}</span> market cap, the median daily volume is <span className="text-amber-300 font-mono font-semibold">{fmtUsd(forecast.forecast.dailyVolumeP50Usd)}</span> after your <span className="text-white">{forecast.listingTier.label}</span> multiplier.
                    </p>
                    <p className="text-xs text-gray-300 leading-relaxed">
                      The realistic range is <span className="text-cyan-300 font-mono">{fmtUsd(forecast.forecast.dailyVolumeP10Usd)} – {fmtUsd(forecast.forecast.dailyVolumeP90Usd)}/day</span> — 80% of similar tokens trade somewhere in this band. Translates to <span className="text-emerald-300 font-mono font-semibold">{fmtUsd(forecast.forecast.monthlyVolumeP50Usd)}/month</span> at the median.
                    </p>
                    {topComparable && (
                      <p className="text-xs text-gray-400 leading-relaxed">
                        Strongest comparable: <span className="text-white font-semibold">{topComparable.symbol}</span> ({topComparable.name}) at <span className="font-mono">{fmtUsd(topComparable.median30dVolumeUsd)}/day</span> — sanity-check this fits your project's profile before quoting these numbers to investors or market makers.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Daily forecast chart */}
              <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5">
                <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-amber-400" /> Daily volume forecast with confidence bands
                </h3>
                <p className="text-xs text-gray-500 mb-4">
                  Three lines = three percentiles across comparable tokens' last 30 days. P50 is the median; P10/P90 show what 80% of similar tokens fall between. Listing-tier multiplier already applied.
                </p>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="bandGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.25} />
                          <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="label" tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => fmtUsd(v)} />
                      <Tooltip
                        contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px' }}
                        formatter={(v, name) => [fmtUsd(v), name === 'p50' ? 'Median (P50)' : name === 'p90' ? 'High (P90)' : 'Low (P10)']}
                      />
                      <Area type="monotone" dataKey="p90" stroke="#fbbf24" strokeWidth={1.5} fill="url(#bandGrad)" />
                      <Area type="monotone" dataKey="p50" stroke="#f59e0b" strokeWidth={2.5} fill="none" />
                      <Area type="monotone" dataKey="p10" stroke="#d97706" strokeWidth={1.5} fill="none" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Comparables used */}
              <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5">
                <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-400" /> Comparables used ({forecast.comparablesUsed.length})
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Real tokens in <strong>{forecast.category.label}</strong> within ±4× of your {fmtUsd(forecast.targetMarketCapUsd)} target. The forecast is just statistics on these tokens' actual recent volume.
                </p>
                <div className="space-y-1.5">
                  {forecast.comparablesUsed.map(c => (
                    <div key={c.id} className="flex items-center justify-between gap-3 bg-dark-900/40 rounded-lg px-3 py-2 border border-dark-700/40">
                      <div className="flex items-center gap-2 min-w-0">
                        {c.image && <img src={c.image} alt="" className="w-5 h-5 rounded-full flex-shrink-0" />}
                        <div className="min-w-0">
                          <p className="text-sm text-white font-semibold truncate">{c.symbol}</p>
                          <p className="text-[10px] text-gray-500 truncate">{c.name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-white font-mono">{fmtUsd(c.marketCapUsd)}</p>
                        <p className="text-[10px] text-gray-500 font-mono">vol: {fmtUsd(c.median30dVolumeUsd)}/d</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Disclaimer */}
              <div className="bg-dark-900/40 rounded-xl border border-dark-700/40 p-4">
                <div className="flex items-start gap-2">
                  <Info className="w-3.5 h-3.5 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div className="text-[11px] text-gray-500 leading-relaxed space-y-1">
                    <p>{forecast.disclaimer}</p>
                    <p><strong className="text-gray-400">Data window:</strong> {forecast.dataSource.windowDescription}.</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, subtext, accent }) {
  const color = accent === 'amber' ? 'text-amber-300' : accent === 'cyan' ? 'text-cyan-300' : accent === 'emerald' ? 'text-emerald-400' : 'text-white';
  return (
    <div className="bg-dark-800/60 rounded-xl border border-dark-700/50 p-4">
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-xl font-bold font-mono mt-1 ${color}`}>{value}</p>
      {subtext && <p className="text-[10px] text-gray-500 mt-1">{subtext}</p>}
    </div>
  );
}
